import React, { useCallback, useEffect, useRef, startTransition } from 'react';
import { connect } from 'react-redux';

import { makeGetFetchedState, makeGetLoadingState } from 'selectors/global';

import { fetchTasks } from 'actions/tasks';

const TaskGroupTasksFetcher = ({ id, type, isStatic, projectId, dontFetch, includeSubtasks, tasksFetched, tasksLoading, onLoad, provideController, children }) => {
    const fetchedCount = useRef(0);

    const loadTasks = useCallback(async (offset = 0) => {
        const payload = {};

        if (isStatic) {
            payload.projectId = projectId;
            payload[type] = true;
            payload.offset = offset;

            if (type === 'archived') {
                payload.order = 'archivedOn:DESC';
            } else if (type === 'completed') {
                payload.archived = false;
                payload.order = 'completedOn:DESC';
            }
        } else {
            payload.taskGroupId = id;
            payload.includeSubtasks = true;
            payload.completed = false;
        }

        if (includeSubtasks) {
            payload.includeSubtasks = true;
        }

        const result = await fetchTasks(payload, type !== 'archived');
        fetchedCount.current += (result?.tasks?.length || 0);

        return result
    }, [isStatic, type, projectId, id, fetchedCount]);

    startTransition(() => {
        if ((id || projectId) && !tasksFetched && !tasksLoading && !dontFetch) {
            loadTasks();
        }
    });

    useEffect(() => {
        if (tasksFetched && onLoad) {
            onLoad();
        }
    }, [tasksFetched, onLoad]);

    useEffect(() => {
        const ctrl = {
            loadTasks: (offset = fetchedCount.current) => {
                return loadTasks(offset);
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, []);

    if (!children) {
        return null;
    }

    return children({ loading: tasksLoading, fetched: tasksFetched });
}

const makeMapStateToProps = () => {
    const getTasksFetched = makeGetFetchedState();
    const getTasksLoading = makeGetLoadingState();

    return ({ tasks }, ownProps) => {
        const association = ownProps.isStatic ? ownProps.type : 'taskGroup';
        const associationId = ownProps.isStatic ? ownProps.projectId : ownProps.id;

        return {
            tasksFetched: !ownProps.dontFetch && getTasksFetched(tasks.fetchInfo, association, associationId),
            tasksLoading: !ownProps.dontFetch && getTasksLoading(tasks.fetchInfo, association, associationId)
        }
    }
}

export default connect(makeMapStateToProps)(TaskGroupTasksFetcher);
